/** Matches server `assertSlugShape` in listing-market-seo.validator.ts */
export const LISTING_SLUG_MIN_LEN = 3;
export const LISTING_SLUG_MAX_LEN = 128;
export const LISTING_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Normalizes arbitrary text into a listing SEO slug segment.
 * Collapses punctuation/separators (–, |, /, etc.) into single hyphens.
 */
export function normalizeListingSlug(input: string): string {
  const normalized = (input ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019\u201C\u201D'"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  if (!normalized) return "";

  const trimmed = normalized.slice(0, LISTING_SLUG_MAX_LEN).replace(/-+$/g, "");
  return trimmed.replace(/^-|-$/g, "");
}

export function isValidListingSlug(slug: string): boolean {
  const s = slug.trim();
  return (
    s.length >= LISTING_SLUG_MIN_LEN &&
    s.length <= LISTING_SLUG_MAX_LEN &&
    LISTING_SLUG_PATTERN.test(s)
  );
}

/** Generates a URL-friendly slug from a title or free-form slug input. */
export function generateSlug(title: string): string {
  return normalizeListingSlug(title);
}

/**
 * Returns a normalized slug; when the result is still invalid and a fallback
 * title is provided, derives the slug from that title instead.
 */
export function ensureValidListingSlug(
  input: string,
  fallbackTitle?: string
): string {
  const normalized = normalizeListingSlug(input);
  if (isValidListingSlug(normalized)) return normalized;

  const fromFallback = fallbackTitle ? normalizeListingSlug(fallbackTitle) : "";
  if (isValidListingSlug(fromFallback)) return fromFallback;

  return normalized;
}
